import { For, type JSX } from 'solid-js';
import { t } from '../i18n';
import { groupScopes, readOnlyScopes } from '../lib/tokens';
import { Field } from './Field';
import { Button } from './ui/Button';

// The mint modal's scope picker, GROUPED BY RESOURCE (#2829).
//
// It was a flat scroll box of every mintable scope — 23 of them on an owner
// session, each a `read:`/`write:` prefix over a noun, in one column. Nothing
// about that list is scannable: the strings differ in their last word, the pair
// belonging to one resource is two rows apart, and the box shows eight at a time.
// `groupScopes` folds them onto the resource they act on and the layout puts a
// group's read and write side by side, so the picker reads as the twelve
// decisions it actually is rather than as twenty-three checkboxes.
//
// Scopes still render VERBATIM — they are the API's own words, the strings a
// maintainer meets again in a 403 detail and in `packages/mcp`'s verb table, so
// a prettified label would be a second name for one thing. The GROUP heading is
// derived from the scope, never translated, for the same reason.
//
// Presenter: the selection lives in `<MintTokenModal/>`, which owns the submit
// and the disclosed call.

export type MintScopePickerProps = {
  /** The scopes this caller may mint (the server's vocabulary, in its order). */
  readonly mintable: readonly string[];
  readonly selected: ReadonlySet<string>;
  /** Replace the whole selection — the two presets. */
  readonly onSelect: (scopes: readonly string[]) => void;
  readonly onToggle: (scope: string) => void;
};

export function MintScopePicker(props: MintScopePickerProps): JSX.Element {
  return (
    <Field
      as="fieldset"
      label={t('account_tokens.scopes_label')}
      id="mint-token-scopes"
      desc={t('account_tokens.scopes_desc')}
    >
      {() => (
        <>
          <div class="mint-token__presets">
            <Button variant="outline" size="sm" onClick={() => props.onSelect(props.mintable)}>
              {t('account_tokens.preset_all')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => props.onSelect(readOnlyScopes(props.mintable))}
            >
              {t('account_tokens.preset_read_only')}
            </Button>
          </div>
          <div class="mint-token__list">
            <For each={groupScopes(props.mintable)}>
              {(group) => (
                <div class="mint-token__group">
                  <p class="mint-token__group-name">{group.group}</p>
                  <div class="mint-token__group-scopes">
                    <For each={group.scopes}>
                      {(scope) => (
                        <label class="mint-token__scope">
                          <input
                            type="checkbox"
                            checked={props.selected.has(scope)}
                            onChange={() => props.onToggle(scope)}
                          />
                          <code>{scope}</code>
                        </label>
                      )}
                    </For>
                  </div>
                </div>
              )}
            </For>
          </div>
        </>
      )}
    </Field>
  );
}
